import { useMemo } from 'react';
import { Globe } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartUnavailable } from '@/components/charts/ChartUnavailable';
import { useApp } from '@/hooks/use-app-context';
import { translations } from '@/translations/translations';
import { MapLegend } from './MapLegend';
import { paintMap, rankCountries } from './map-scale';
import { CountryRanking } from './CountryRanking';
import { WorldMap } from './WorldMap';
import type { ContributorCountry } from './types';

/**
 * How many countries the map names directly, on top of the hover layer. One:
 * the collection sits in neighbouring countries (Togo, Benin, Ghana) whose
 * names overlapped at this scale; the ranking below names the others.
 */
const LABELLED_COUNTRIES = 1;

interface ContributorMapProps {
  /** Contributors per country (registered profiles). */
  countries?: readonly ContributorCountry[];
  /** Distinct countries the platform counted, which can exceed the listed ones. */
  countriesCount?: number;
  isLoading?: boolean;
  /** Overrides of the default wording, for a map of something else than profiles. */
  labels?: {
    title?: string;
    subtitle?: string;
    /** What a value counts, in the tooltip and the ranking ("contributions"). */
    unit?: string;
    /** Word next to the country count ("pays"). */
    countLabel?: string;
  };
}

export function ContributorMap({
  countries: reported,
  countriesCount,
  isLoading = false,
  labels = {},
}: ContributorMapProps) {
  const { language } = useApp();
  const t = translations[language];
  const map = useMemo(() => paintMap(reported ?? []), [reported]);
  const ranked = useMemo(() => rankCountries(map), [map]);
  const formatValue = (value: number) => value.toLocaleString(language === 'fr' ? 'fr-FR' : 'en-US');
  const unit = labels.unit ?? t.contributorMapContributors;

  return (
    <Card className="overflow-hidden rounded-3xl border-border bg-surface shadow-none">
      <CardHeader className="pb-2">
        <div className="flex items-end justify-between gap-6 border-b border-border/40 pb-5">
          <CardTitle className="flex items-center gap-3 text-base font-medium">
            <span className="flex h-8 w-8 items-center justify-center rounded-full border border-primary/15 bg-primary/5 text-primary">
              <Globe className="h-3.5 w-3.5" />
            </span>

            <span className="flex flex-col gap-1">
              <span className="text-[15px] font-medium tracking-[-0.02em] text-foreground">
                {labels.title ?? t.contributorMapChart}
              </span>
              <span className="text-xs font-normal tracking-wide text-muted-foreground">
                {labels.subtitle ?? t.contributorMapSubtitle}
              </span>
            </span>
          </CardTitle>

          {/* No count while loading: a 0 there would read as "no country". */}
          {!isLoading && (
            <div className="flex flex-col items-end gap-1">
              <span className="text-sm font-medium tracking-tight text-foreground">
                {formatValue(countriesCount ?? ranked.length)}
              </span>
              <span className="flex items-center gap-2 text-[9px] uppercase tracking-[0.28em] text-muted-foreground">
                {labels.countLabel ?? t.contributorMapActiveRegions}
              </span>
            </div>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-5">
        {isLoading || ranked.length === 0 ? (
          <ChartUnavailable isLoading={isLoading} />
        ) : (
          <>
            <WorldMap
              countries={map.countries}
              labelled={ranked.slice(0, LABELLED_COUNTRIES)}
              formatValue={formatValue}
              contributorsLabel={unit}
              shareLabel={t.contributorMapShare}
              zoomInLabel={t.contributorMapZoomIn}
              zoomOutLabel={t.contributorMapZoomOut}
              resetLabel={t.contributorMapReset}
              fitLabel={t.contributorMapFit}
            />

            <MapLegend
              noDataLabel={t.contributorMapNoData}
              lowLabel={t.contributorMapLow}
              highLabel={t.contributorMapHigh}
            />

            <CountryRanking
              countries={ranked}
              unmapped={map.unmapped}
              total={map.total}
              formatValue={formatValue}
              contributorsLabel={unit}
              outsideLabel={t.contributorMapOutsideAfrica}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}
